import { zodResolver } from '@hookform/resolvers/zod';
import { CreateDriverSchema, type CreateDriver } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Camera, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, Dialog, EmptyState, Field, PageHeader, Pagination, Skeleton, Spinner, Table, Td, Th, useToast } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';
import { fmtDate, toIsoDate } from '../../lib/format';
import { uploadFile } from '../../lib/upload';

export interface DriverRow {
  id: string;
  fullName: string;
  phone: string;
  nationalId: string | null;
  licenseNumber: string | null;
  licenseClass: string | null;
  licenseExpiresAt: string | null;
  status: 'ACTIVE' | 'ON_LEAVE' | 'SUSPENDED' | 'TERMINATED';
  hiredAt: string | null;
  notes: string | null;
  photoFileId: string | null;
  photoUrl: string | null;
  currentVehicle: { id: string; plateAr: string } | null;
  user: { lastLoginAt: string | null; status: string } | null;
}

const initials = (name: string) => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? '').join('');

export function DriverAvatar({ name, photoUrl, size = 'md' }: { name: string; photoUrl: string | null; size?: 'md' | 'lg' }) {
  const cls = clsx('shrink-0 overflow-hidden rounded-full bg-accent font-bold text-accent-ink flex items-center justify-center', size === 'lg' ? 'size-16 text-xl' : 'size-9 text-xs');
  return photoUrl ? <img src={photoUrl} alt={name} className={clsx(cls, 'object-cover')} /> : <span className={cls} aria-hidden>{initials(name)}</span>;
}

export const driverTone = (s: DriverRow['status']) => (s === 'ACTIVE' ? 'success' : s === 'ON_LEAVE' ? 'info' : s === 'SUSPENDED' ? 'warning' : 'neutral') as 'success' | 'info' | 'warning' | 'neutral';
const licenseTone = (d: string | null): 'danger' | 'warning' | 'neutral' => {
  if (!d) return 'neutral';
  const days = (new Date(d).getTime() - Date.now()) / 86_400_000;
  return days < 0 ? 'danger' : days < 30 ? 'warning' : 'neutral';
};

export function DriversPage() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<DriverRow | null>(null);
  const q = useQuery({ queryKey: ['drivers', page, search, status], queryFn: () => api<Paginated<DriverRow>>(`/drivers${qs({ page, q: search, status })}`) });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('nav.drivers')} subtitle={q.data ? t('common.totalCount', { count: q.data.meta.total }) : undefined} actions={<Can perm="drivers:create"><button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> {t('drivers.add')}</button></Can>} />
      <div className="flex flex-wrap gap-2">
        <input className="input max-w-xs" placeholder={t('drivers.searchPlaceholder')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} aria-label={t('common.search')} />
        <select className="input max-w-44" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label={t('settings.users.status')}>
          <option value="">{t('common.all')}</option>
          {(['ACTIVE', 'ON_LEAVE', 'SUSPENDED', 'TERMINATED'] as const).map((s) => <option key={s} value={s}>{t(`drivers.statuses.${s}`)}</option>)}
        </select>
      </div>
      {q.isLoading ? <Skeleton className="h-72" /> : !q.data?.data.length ? <div className="card"><EmptyState title={t('drivers.empty')} hint={t('drivers.emptyHint')} /></div> : (
        <Table>
          <thead><tr><Th>{t('drivers.name')}</Th><Th>{t('drivers.phone')}</Th><Th>{t('drivers.vehicle')}</Th><Th>{t('drivers.license')}</Th><Th>{t('drivers.lastLogin')}</Th><Th>{t('settings.users.status')}</Th><Th className="w-1" sticky="end" /></tr></thead>
          <tbody>
            {q.data.data.map((d) => (
              <tr key={d.id} className="group hover:bg-surface-hover">
                <Td><Link to={`/drivers/${d.id}`} className="flex items-center gap-3 font-bold text-brand-ink hover:underline"><DriverAvatar name={d.fullName} photoUrl={d.photoUrl} />{d.fullName}</Link></Td>
                <Td><span dir="ltr">{d.phone}</span></Td>
                <Td>{d.currentVehicle ? <Link to={`/vehicles/${d.currentVehicle.id}`} className="hover:underline">{d.currentVehicle.plateAr}</Link> : <span className="text-text-faint">—</span>}</Td>
                <Td>{d.licenseNumber ? <span dir="ltr">{d.licenseNumber}</span> : '—'}{d.licenseExpiresAt ? <div><Badge tone={licenseTone(d.licenseExpiresAt)}>{fmtDate(d.licenseExpiresAt, i18n.language)}</Badge></div> : null}</Td>
                <Td className="text-text-muted">{d.user?.lastLoginAt ? fmtDate(d.user.lastLoginAt, i18n.language) : t('drivers.neverLoggedIn')}</Td>
                <Td><Badge tone={driverTone(d.status)} dot>{t(`drivers.statuses.${d.status}`)}</Badge></Td>
                <Td className="text-end" sticky="end"><Can perm="drivers:update"><button className="btn-ghost size-8 !p-0" onClick={() => setEditing(d)} aria-label={t('common.edit')} title={t('common.edit')}><Pencil size={14} /></button></Can></Td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={7}><Pagination page={q.data.meta.page} pageSize={q.data.meta.pageSize} total={q.data.meta.total} onPage={setPage} /></td></tr></tfoot>
        </Table>
      )}
      {creating ? <DriverDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <DriverDialog driver={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

export function DriverDialog({ driver, onClose }: { driver?: DriverRow; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const isNew = !driver;
  const [photo, setPhoto] = useState<{ fileId: string | null; url: string | null }>({ fileId: driver?.photoFileId ?? null, url: driver?.photoUrl ?? null });
  const [uploading, setUploading] = useState(false);
  const form = useForm<CreateDriver>({
    resolver: zodResolver(isNew ? CreateDriverSchema : CreateDriverSchema.partial()),
    defaultValues: driver ? { fullName: driver.fullName, phone: driver.phone, nationalId: driver.nationalId ?? '', licenseNumber: driver.licenseNumber ?? '', licenseClass: driver.licenseClass ?? '', licenseExpiresAt: toIsoDate(driver.licenseExpiresAt) || null, hiredAt: toIsoDate(driver.hiredAt) || null, notes: driver.notes ?? '' } : {},
  });
  const save = useMutation({
    mutationFn: (v: CreateDriver) => {
      const body = { ...v, nationalId: v.nationalId || undefined, licenseNumber: v.licenseNumber || undefined, licenseClass: v.licenseClass || undefined, licenseExpiresAt: v.licenseExpiresAt || null, hiredAt: v.hiredAt || null, notes: v.notes || undefined, photoFileId: photo.fileId };
      return isNew ? api('/drivers', { method: 'POST', json: body }) : api(`/drivers/${driver.id}`, { method: 'PATCH', json: body });
    },
    onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['drivers'] }); void qc.invalidateQueries({ queryKey: ['driver', driver?.id] }); onClose(); },
    onError: toast.error,
  });
  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const fileId = await uploadFile(file, 'driver_photo');
      setPhoto({ fileId, url: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(err);
    } finally {
      setUploading(false);
    }
  };
  const e = form.formState.errors;
  const name = form.watch('fullName') || driver?.fullName || '';
  return (
    <Dialog open onClose={onClose} title={isNew ? t('drivers.add') : t('drivers.edit')} size="lg" footer={<><button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button><button className="btn-primary" form="driver-form" type="submit" disabled={save.isPending || uploading}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button></>}>
      <form id="driver-form" onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-4 sm:grid-cols-2" noValidate>
        <div className="flex items-center gap-4 sm:col-span-2">
          <DriverAvatar name={name || '?'} photoUrl={photo.url} size="lg" />
          <div className="flex flex-wrap gap-2">
            <label className={clsx('btn-secondary cursor-pointer', uploading && 'pointer-events-none opacity-60')}>
              {uploading ? <Spinner /> : <Camera size={16} />} {photo.url ? t('drivers.changePhoto') : t('drivers.addPhoto')}
              <input type="file" accept="image/*" className="sr-only" onChange={(ev) => { void onPhoto(ev.target.files?.[0]); ev.target.value = ''; }} />
            </label>
            {photo.url ? <button type="button" className="btn-ghost text-danger-fg" onClick={() => setPhoto({ fileId: null, url: null })}><Trash2 size={16} /> {t('drivers.removePhoto')}</button> : null}
          </div>
        </div>
        <Field label={t('drivers.name')} required error={e.fullName?.message}>{(id) => <input id={id} className="input" {...form.register('fullName')} />}</Field>
        <Field label={t('drivers.phone')} required error={e.phone?.message} hint={t('drivers.phoneHint')}>{(id) => <input id={id} dir="ltr" className="input" placeholder="01xxxxxxxxx" {...form.register('phone')} />}</Field>
        <Field label={t('drivers.nationalId')} error={e.nationalId?.message}>{(id) => <input id={id} dir="ltr" className="input" {...form.register('nationalId')} />}</Field>
        <Field label={t('drivers.licenseNumber')} error={e.licenseNumber?.message}>{(id) => <input id={id} dir="ltr" className="input" {...form.register('licenseNumber')} />}</Field>
        <Field label={t('drivers.licenseClass')}>{(id) => <input id={id} className="input" placeholder={t('drivers.licenseClassHint')} {...form.register('licenseClass')} />}</Field>
        <Field label={t('drivers.licenseExpiresAt')} error={e.licenseExpiresAt?.message}>{(id) => <input id={id} type="date" dir="ltr" className="input" {...form.register('licenseExpiresAt')} />}</Field>
        <Field label={t('drivers.hiredAt')} error={e.hiredAt?.message}>{(id) => <input id={id} type="date" dir="ltr" className="input" {...form.register('hiredAt')} />}</Field>
        <div className="sm:col-span-2"><Field label={t('vehicles.notes')}>{(id) => <textarea id={id} className="input min-h-20" {...form.register('notes')} />}</Field></div>
      </form>
    </Dialog>
  );
}
